import React, { useState, useEffect } from "react";
import { Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { API_BASE_URL, authHeaders } from "../../api";

const emptyCvItem = {
    year: "",
    title: "",
    tooltip: "",
    description: [],
    download: {
        url: "",
        name: ""
    }
};

const createEmptyCvItem = () => ({
    ...emptyCvItem,
    download: { ...emptyCvItem.download }
});

function AdminCV() {

    const [items, setItems] = useState([]);
    const [openIndex, setOpenIndex] = useState(null);
    const [status, setStatus] = useState("");

    const handleEdit = (index) => {
        setOpenIndex((current) => current === index ? null : index);
    };

    useEffect(() => {

        async function loadCV() {

            const res = await fetch(`${API_BASE_URL}/api/cv`, {
                headers: authHeaders()
            });

            const data = await res.json();
            setItems(data);
        }

        loadCV();

    }, []);

    const updateItem = (index, field, value) => {
        setItems((current) => current.map((item, itemIndex) => {
            if (itemIndex !== index) return item;

            return {
                ...item,
                [field]: value
            };
        }));
    };

    const updateDescription = (index, value) => {
        updateItem(index, "description", value.split("\n"));
    };

    const updateDownload = (index, field, value) => {
        setItems((current) => current.map((item, itemIndex) => {
            if (itemIndex !== index) return item;

            return {
                ...item,
                download: {
                    ...(item.download || emptyCvItem.download),
                    [field]: value
                }
            };
        }));
    };

    const addItem = () => {
        setItems((current) => [...current, createEmptyCvItem()]);
        setOpenIndex(items.length);
    };

    const removeItem = (index) => {
        setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
        setOpenIndex(null);
    };

    const saveCV = async () => {
        setStatus("Speichert...");

        const payload = items.map((item) => {
            const downloadUrl = item.download?.url?.trim() || "";
            const downloadName = item.download?.name?.trim() || "";

            return {
                year: item.year,
                title: item.title,
                tooltip: item.tooltip,
                description: item.description.filter((line) => line.trim()),
                ...(downloadUrl || downloadName
                    ? { download: { url: downloadUrl, name: downloadName } }
                    : {})
            };
        });

        try {
            const response = await fetch(`${API_BASE_URL}/api/cv`, {
                method: "PUT",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error("Speichern fehlgeschlagen");
            }

            setItems(payload);
            setStatus("Gespeichert");
        } catch (error) {
            console.error(error);
            setStatus("Fehler beim Speichern");
        }
    };

    return (
        <div className="action-card">
            <h3>Lebenslauf</h3>
            <div className="action-card-body">
                {items.map((item, index) => (
                    <div className="cv-edit-item" key={index}>
                        <div className="cv-edit-header">
                            <h3>{item.year} - {item.title}</h3>
                            <button
                                className="icon-button"
                                type="button"
                                title={openIndex === index ? "Schließen" : "Bearbeiten"}
                                onClick={() => handleEdit(index)}
                            >
                                {openIndex === index ? <X size={20} /> : <Pencil size={20} />}
                            </button>
                        </div>

                        {openIndex === index && (
                            <div className="cv-edit-area">
                                <div className="form-grid">
                                    <label className="field-label" htmlFor={`cv-year-${index}`}>Jahr</label>
                                    <input
                                        id={`cv-year-${index}`}
                                        className="text-input"
                                        value={item.year}
                                        onChange={(event) => updateItem(index, "year", event.target.value)}
                                    />
                                    <label className="field-label" htmlFor={`cv-title-${index}`}>Titel</label>
                                    <input
                                        id={`cv-title-${index}`}
                                        className="text-input"
                                        value={item.title}
                                        onChange={(event) => updateItem(index, "title", event.target.value)}
                                    />
                                    <label className="field-label" htmlFor={`cv-tooltip-${index}`}>Kurzinfo</label>
                                    <input
                                        id={`cv-tooltip-${index}`}
                                        className="text-input"
                                        value={item.tooltip}
                                        onChange={(event) => updateItem(index, "tooltip", event.target.value)}
                                    />
                                </div>
                                <label className="field-label" htmlFor={`cv-description-${index}`}>Beschreibung</label>
                                <textarea
                                    id={`cv-description-${index}`}
                                    className="textarea"
                                    value={(item.description || []).join("\n")}
                                    onChange={(event) => updateDescription(index, event.target.value)}
                                    style={{ height: "170px" }}
                                />
                                <div className="form-grid">
                                    <label className="field-label" htmlFor={`cv-download-url-${index}`}>Download-URL</label>
                                    <input
                                        id={`cv-download-url-${index}`}
                                        className="text-input"
                                        value={item.download?.url || ""}
                                        onChange={(event) => updateDownload(index, "url", event.target.value)}
                                    />
                                    <label className="field-label" htmlFor={`cv-download-name-${index}`}>Download-Name</label>
                                    <input
                                        id={`cv-download-name-${index}`}
                                        className="text-input"
                                        value={item.download?.name || ""}
                                        onChange={(event) => updateDownload(index, "name", event.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-outline-danger icon-label-button"
                                    onClick={() => removeItem(index)}
                                >
                                    <Trash2 size={18} />
                                    Entfernen
                                </button>
                            </div>
                        )}

                    </div>
                ))}
                <div className="admin-actions">
                    <button type="button" className="btn btn-secondary icon-label-button" onClick={addItem}>
                        <Plus size={18} />
                        Eintrag hinzufügen
                    </button>
                    <button type="button" className="btn btn-success icon-label-button" onClick={saveCV}>
                        <Save size={18} />
                        Speichern
                    </button>
                    {status && <span className="save-status">{status}</span>}
                </div>

            </div>
        </div>
    );
}

export default AdminCV;
